
import { useState, useEffect } from 'react';

export default function Hero() {
  const [text, setText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isTerminalOpen, setIsTerminalOpen] = useState(true);
  const fullText = 'Software Engineer';

  useEffect(() => {
    setIsVisible(true);
    
    let i = 0;
    const timer = setInterval(() => {
      if (i < fullText.length) {
        setText(fullText.slice(0, i + 1));
        i++;
      } else {
        clearInterval(timer);
      }
    }, 100);

    const cursorTimer = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);

    return () => {
      clearInterval(timer);
      clearInterval(cursorTimer);
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const minimizeWindow = () => {
    setIsFullscreen(false);
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center px-4 overflow-hidden">
      {/* Matrix rain background */}
      <div className="matrix-rain">
        {[...Array(50)].map((_, i) => (
          <div
            key={i}
            className="matrix-char"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 8}s`,
              animationDuration: `${6 + Math.random() * 4}s`
            }}
          >
            {String.fromCharCode(0x30A0 + Math.random() * 96)}
          </div>
        ))}
      </div>

      {/* Fullscreen overlay */}
      {isFullscreen && (
        <div className="fixed inset-0 bg-black/95 z-50 flex flex-col animate-slide-in-up">
          {/* Fullscreen terminal header */}
          <div className="bg-gray-900 border-b border-green-500/30 p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse cursor-pointer hover:bg-red-400" onClick={minimizeWindow}></div>
                <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse cursor-pointer hover:bg-yellow-400" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse cursor-pointer hover:bg-green-400" style={{ animationDelay: '0.4s' }} onClick={toggleFullscreen}></div>
                <span className="text-green-400 text-sm ml-4 animate-pulse">~/portfolio - Fullscreen Terminal</span>
              </div>
              <button 
                onClick={minimizeWindow}
                className="text-green-400 hover:text-green-300 transition-colors duration-300"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          </div>

          {/* Fullscreen terminal content */}
          <div className="flex-1 bg-black p-8 overflow-auto">
            <div className="max-w-6xl mx-auto text-left space-y-6">
              <div className="text-green-400 animate-slide-in-left">
                <span className="text-green-500 animate-pulse">$</span> whoami --verbose
              </div>
              <div className="text-white pl-4 animate-slide-in-right" style={{ animationDelay: '0.3s' }}>
                <h1 className="text-6xl md:text-8xl font-bold mb-6 gradient-text">
                  {text}
                  <span className={`${showCursor ? 'opacity-100' : 'opacity-0'} transition-opacity text-green-500`}>|</span>
                </h1>
                <p className="text-2xl text-gray-300 mb-2">
                  Tech-agnostic software engineer & pragmatic problem solver
                </p>
                <p className="text-lg text-gray-400">
                  From React frontends to Go and C/C++ backends, I choose the right tools to ship reliable solutions.
                </p>
              </div>
              
              <div className="text-green-400 mt-8 animate-slide-in-left" style={{ animationDelay: '0.6s' }}>
                <span className="text-green-500 animate-pulse">$</span> cat detailed_profile.txt
              </div>
              <div className="text-gray-300 pl-4 space-y-4 animate-slide-in-right text-lg" style={{ animationDelay: '0.9s' }}>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-green-400 text-xl mb-3">Core Specializations:</h3>
                    {[
                      '• Building React/Next.js experiences backed by Go & TypeScript APIs',
                      '• Designing and shipping AI & LLM products (LangChain, LangGraph, RAG)',
                      '• End-to-end problem solving: from idea → architecture → production',
                      '• System design, scalability, and performance tuning',
                      '• C/C++ low-level programming when performance really matters'
                    ].map((item, index) => (
                      <p key={index} className="hover:text-green-400 transition-colors duration-300 hover:translate-x-2 transform transition-transform mb-2" style={{ animationDelay: `${1.2 + index * 0.1}s` }}>
                        {item}
                      </p>
                    ))}
                  </div>
                  <div>
                    <h3 className="text-green-400 text-xl mb-3">Tech Stack Mastery:</h3>
                    {[
                      '• React & Next.js (TypeScript-first)',
                      '• Golang services and APIs',
                      '• LangChain & LangGraph for LLM-powered workflows',
                      '• Node/NestJS & Python for backend and tooling',
                      '• C/C++ for performance-sensitive components',
                      '• Containers, Linux, and cloud-native workflows'
                    ].map((item, index) => (
                      <p key={index} className="hover:text-green-400 transition-colors duration-300 hover:translate-x-2 transform transition-transform mb-2" style={{ animationDelay: `${1.5 + index * 0.1}s` }}>
                        {item}
                      </p>
                    ))}
                  </div>
                </div>

                <div className="mt-6">
                  <h3 className="text-green-400 text-xl mb-3">Architectures & Systems Thinking:</h3>
                  {[
                    '• Event-driven and message-oriented systems (Kafka, RabbitMQ)',
                    '• Microservices & service-oriented architectures',
                    '• Hexagonal / Clean architecture for maintainable codebases',
                    '• Streaming & search pipelines (Kafka Streams, Elasticsearch)',
                    '• Resilient, observable systems with queues, retries, and monitoring'
                  ].map((item, index) => (
                    <p
                      key={index}
                      className="hover:text-green-400 transition-colors duration-300 hover:translate-x-2 transform transition-transform mb-2"
                      style={{ animationDelay: `${1.9 + index * 0.1}s` }}
                    >
                      {item}
                    </p>
                  ))}
                </div>
              </div>

              <div className="text-green-400 mt-8 animate-slide-in-left" style={{ animationDelay: '2s' }}>
                <span className="text-green-500 animate-pulse">$</span> ls -la achievements/
              </div>
              <div className="text-gray-300 pl-4 animate-slide-in-right" style={{ animationDelay: '2.3s' }}>
                <div className="grid md:grid-cols-3 gap-6 mt-4">
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 hover-glow">
                    <div className="text-3xl font-bold text-green-400 gradient-text">5+</div>
                    <div className="text-sm">Years Experience</div>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 hover-glow">
                    <div className="text-3xl font-bold text-green-400 gradient-text">50+</div>
                    <div className="text-sm">Projects Completed</div>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 hover-glow">
                    <div className="text-3xl font-bold text-green-400 gradient-text">10+</div>
                    <div className="text-sm">Technologies Mastered</div>
                  </div>
                </div>
              </div>

              <div className="text-green-400 mt-8 animate-slide-in-left" style={{ animationDelay: '2.6s' }}>
                <span className="text-green-500 animate-pulse">$</span> ./navigate_portfolio.sh
              </div>
              
              <div className="flex flex-wrap gap-4 mt-6 pl-4 animate-slide-in-up" style={{ animationDelay: '2.9s' }}>
                {[
                  { text: 'About Me', section: 'about', icon: 'ri-user-line' },
                  { text: 'Projects', section: 'projects', icon: 'ri-code-box-line' },
                  { text: 'Contact', section: 'contact', icon: 'ri-mail-line' }
                ].map((button, index) => (
                  <button 
                    key={button.section}
                    onClick={() => {
                      minimizeWindow();
                      setTimeout(() => scrollToSection(button.section), 300);
                    }}
                    className="bg-green-500/20 hover:bg-green-500/30 text-green-400 px-8 py-3 rounded border border-green-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-green-500/20 whitespace-nowrap cursor-pointer hover-scale group animate-float text-lg"
                    style={{ animationDelay: `${3.2 + index * 0.1}s` }}
                  >
                    <i className={`${button.icon} mr-2 group-hover:animate-pulse`}></i>
                    {button.text}
                  </button>
                ))}
              </div>

              <div className="text-green-400 mt-8 animate-slide-in-left" style={{ animationDelay: '3.6s' }}>
                <span className="text-green-500 animate-pulse">$</span> curl -O cv.pdf
              </div>
              <div className="pl-4 mt-4 animate-slide-in-right" style={{ animationDelay: '3.9s' }}>
                <a
                  href="https://drive.google.com/file/d/1wiFDZuY7Yt-np7SWVnh5NT7AT8bukqkK/view?usp=drive_link"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-green-500/20 hover:bg-green-500/30 text-green-400 px-8 py-3 rounded border border-green-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-green-500/20 whitespace-nowrap cursor-pointer hover-scale group animate-float"
                >
                  <i className="ri-download-cloud-2-line mr-2 group-hover:animate-pulse"></i>
                  Download CV
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={`max-w-4xl mx-auto px-4 text-center relative z-10 transition-all duration-1000 ${isVisible ? 'animate-slide-in-up' : 'opacity-0'} ${isFullscreen ? 'opacity-0 pointer-events-none' : ''}`}>
        {/* Terminal window header with glow effect */}
        <div className="bg-gray-900/80 border border-green-500/30 rounded-t-lg p-3 mb-0 backdrop-blur-md hover-glow">
          <div className="flex items-center space-x-2">
            <div
              className="w-3 h-3 bg-red-500 rounded-full animate-pulse cursor-pointer hover:bg-red-400"
              onClick={() => setIsTerminalOpen(prev => !prev)}
            ></div>
            <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse cursor-pointer hover:bg-yellow-400" style={{ animationDelay: '0.2s' }}></div>
            <div 
              className="w-3 h-3 bg-green-500 rounded-full animate-pulse animate-glow cursor-pointer hover:bg-green-400 hover:scale-110 transition-all duration-300" 
              style={{ animationDelay: '0.4s' }}
              onClick={toggleFullscreen}
            ></div>
            <span className="text-green-400 text-sm ml-4 animate-pulse">~/portfolio</span>
          </div>
        </div>

        {/* Terminal content with enhanced effects */}
        {isTerminalOpen && (
        <div className="bg-black/90 border-x border-b border-green-500/30 rounded-b-lg p-8 backdrop-blur-md hover-glow">
          <div className="text-left space-y-4">
            <div className="text-green-400 animate-slide-in-left">
              <span className="text-green-500 animate-pulse">$</span> whoami
            </div>
            <div className="text-white pl-4 animate-slide-in-right" style={{ animationDelay: '0.3s' }}>
              <h1 className="text-4xl md:text-6xl font-bold mb-4 gradient-text">
                {text}
                <span className={`${showCursor ? 'opacity-100' : 'opacity-0'} transition-opacity text-green-500`}>|</span>
              </h1>
            </div>
            
            <div className="text-green-400 mt-8 animate-slide-in-left" style={{ animationDelay: '0.6s' }}>
              <span className="text-green-500 animate-pulse">$</span> cat specialization.txt
            </div>
            <div className="text-gray-300 pl-4 space-y-2 animate-slide-in-right" style={{ animationDelay: '0.9s' }}>
              {[
                '• Tech-agnostic mindset',
                '• Excellent problem-solving skills and strong system design foundations'
              ].map((item, index) => (
                <p key={index} className="hover:text-green-400 transition-colors duration-300 hover:translate-x-2 transform transition-transform" style={{ animationDelay: `${1.2 + index * 0.1}s` }}>
                  {item}
                </p>
              ))}
            </div>

            <div className="text-green-400 mt-8 animate-slide-in-left" style={{ animationDelay: '1.8s' }}>
              <span className="text-green-500 animate-pulse">$</span> ls tech_stack/
            </div>
            <div className="text-gray-300 pl-4 animate-slide-in-right" style={{ animationDelay: '2.1s' }}>
              <div className="flex flex-wrap gap-2 mt-2">
                {['React', 'Next.js', 'Golang', 'LangChain', 'LangGraph', 'C/C++', 'TypeScript'].map((tech, index) => (
                  <span 
                    key={tech} 
                    className="bg-green-500/20 text-green-400 px-3 py-1 rounded border border-green-500/30 text-sm hover-scale hover-glow cursor-pointer animate-float"
                    style={{ animationDelay: `${2.4 + index * 0.1}s` }}
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            <div className="text-green-400 mt-8 animate-slide-in-left" style={{ animationDelay: '2.7s' }}>
              <span className="text-green-500 animate-pulse">$</span> ls architectures/
            </div>
            <div className="text-gray-300 pl-4 animate-slide-in-right" style={{ animationDelay: '3s' }}>
              {[
                '• Event-driven and message-oriented systems (Kafka, RabbitMQ)',
                '• Microservices & service-oriented architectures'
              ].map((item, index) => (
                <p
                  key={index}
                  className="hover:text-green-400 transition-colors duration-300 hover:translate-x-2 transform transition-transform"
                  style={{ animationDelay: `${3.3 + index * 0.1}s` }}
                >
                  {item}
                </p>
              ))}
            </div>

            <div className="text-green-400 mt-8 animate-slide-in-left" style={{ animationDelay: '3s' }}>
              <span className="text-green-500 animate-pulse">$</span> ./explore_portfolio.sh
            </div>
            
            <div className="flex flex-wrap gap-4 mt-6 pl-4 animate-slide-in-up" style={{ animationDelay: '3.3s' }}>
              {[
                { text: 'About Me', section: 'about', icon: 'ri-user-line' },
                { text: 'Projects', section: 'projects', icon: 'ri-code-box-line' },
                { text: 'Contact', section: 'contact', icon: 'ri-mail-line' }
              ].map((button, index) => (
                <button 
                  key={button.section}
                  onClick={() => scrollToSection(button.section)}
                  className="bg-green-500/20 hover:bg-green-500/30 text-green-400 px-6 py-2 rounded border border-green-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-green-500/20 whitespace-nowrap cursor-pointer hover-scale group animate-float"
                  style={{ animationDelay: `${3.6 + index * 0.1}s` }}
                >
                  <i className={`${button.icon} mr-2 group-hover:animate-pulse`}></i>
                  {button.text}
                </button>
              ))}
            </div>

              <div className="text-green-400 mt-8 animate-slide-in-left" style={{ animationDelay: '4.2s' }}>
                <span className="text-green-500 animate-pulse">$</span> curl -O cv.pdf
              </div>
              <div className="pl-4 mt-4 animate-slide-in-right" style={{ animationDelay: '4.5s' }}>
                <a
                  href="https://drive.google.com/file/d/1wiFDZuY7Yt-np7SWVnh5NT7AT8bukqkK/view?usp=drive_link"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-green-500/20 hover:bg-green-500/30 text-green-400 px-6 py-2 rounded border border-green-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-green-500/20 whitespace-nowrap cursor-pointer hover-scale group animate-float"
                >
                  <i className="ri-download-cloud-2-line mr-2 group-hover:animate-pulse"></i>
                  Download CV
                </a>
              </div>

            <div className="text-green-400 mt-8 animate-slide-in-left" style={{ animationDelay: '4s' }}>
              <span className="text-gray-500 text-sm">// Click the green button above to maximize terminal</span>
            </div>
          </div>
        </div>
        )}

        {/* Enhanced scroll indicator */}
        { /* <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-green-500/50 rounded-full flex justify-center hover-glow cursor-pointer">
            <div className="w-1 h-3 bg-green-500 rounded-full mt-2 animate-pulse animate-glow"></div>
          </div>
        </div> */ }
      </div>

      {/* Floating code snippets */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {['useEffect()', 'goroutine { }', 'LangChain()', 'graph.run()', 'fetch("/api")'].map((code, index) => (
          <div
            key={index}
            className="absolute text-green-500/20 font-mono text-sm animate-float opacity-50"
            style={{
              left: `${10 + Math.random() * 80}%`,
              top: `${10 + Math.random() * 80}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${4 + Math.random() * 2}s`
            }}
          >
            {code}
          </div>
        ))}
      </div>
    </section>
  );
}
